import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormControl } from '@angular/forms'
import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { head, symmetricDifference } from 'ramda'
import { startWith, SubscriptionLike } from 'rxjs'
import { map, pairwise } from 'rxjs/operators'
import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { ScrollService } from '@app-services'

/**
 * We need this child component in order to be reactive with the `state.items` property within
 * {@link RefinementListComponent}. This reactiveness is in turn required to transform the API
 * from instantseach to the one required by our checkbox components (using a form control for data binding).
 */
@Component({
    selector: 'app-refinement-list-contents',
    styleUrls: ['./refinement-list-contents.component.scss'],
    template: `
        <app-checkbox *ngFor="let item of items"
                      [value]="item.value"
                      [control]="refinements"
                      (click)="onRefinementClick()"
        >
            <span class="refinement-label">
                <span class="refinement-label__text">{{ item.label }}</span>
                <span class="refinement-label__count">{{ item.count }}</span>
            </span>
        </app-checkbox>
    `,
})
export class RefinementListContentsComponent implements OnInit, OnChanges, OnDestroy {

    @Input()
    public localisedAttribute: string

    @Input()
    public items: RefinementListItem[] = []

    @Output()
    public readonly toggle = new EventEmitter<string>()

    public readonly refinements: FormControl = new FormControl([])

    private refinementsChangeSubscription: SubscriptionLike

    constructor(
        private analytics: AnalyticsService,
        private scrollService: ScrollService,
    ) {}

    /**
     * To prevent infinite toggle loops, we have to imperatively maintain a boolean value that blocks emissions
     * caused by form control value changes that were caused by an items input change. If we don't, changes in
     * items that originate from any place other than this component instance causes the form control's value
     * to be out of sync and hence trigger another toggle emission. (Note: { emitEvent: false } will not work
     * when updating the control's value)
     */
    private blockEmissions: boolean = false

    public ngOnInit(): void {
        this.refinementsChangeSubscription = this.refinements.valueChanges.pipe(
            startWith(this.refinements.value),
            pairwise(),
            map(([prevSet, nextSet]) => head<string>(symmetricDifference(prevSet, nextSet))),
        ).subscribe((toggledValue) => {
            if (! this.blockEmissions && toggledValue) {
                const toggledItem = this.refinements.value.find((val: string) => val === toggledValue)
                if (! toggledItem) {
                    this.analytics.logFilterOff('checkbox', this.localisedAttribute, toggledValue)
                }
                this.toggle.emit(toggledValue)
            }
        })
    }

    public onRefinementClick(): void {
        this.scrollService.scrollToElementId('filter-heading')
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.items) {
            this.blockEmissions = true
            this.refinements.setValue(this.pickRefinedItemValues(changes.items.currentValue))
            Promise.resolve().then(() => this.blockEmissions = false)
        }
    }

    public ngOnDestroy(): void {
        this.refinementsChangeSubscription?.unsubscribe()
    }

    private pickRefinedItemValues(items: RefinementListItem[]): string[] {
        return items.reduce<string[]>((refinedItems, { isRefined, value }) => {
            return isRefined
                ? [...refinedItems, value]
                : refinedItems
        }, [])
    }
}
